import React, { useState, useMemo } from 'react';
import cn from 'classnames';

import { useRouter } from 'next/router';
import { IconButton } from 'components';
import { Modal } from 'components/Modal/Modal.component';
import { ButtonV3 } from 'components/ComponentV2';
import { TextArea } from 'components/form';
import { Row } from 'components/layout';
import { useNotification } from 'hooks/useNotification';
import { useChatRoom } from 'hooks/useChatRoom';
import { SponsorShipPlanType } from 'lib/models/sponsorship-plans';
import { useAuth } from 'lib/providers/AuthProvider';
import { PAGE_ROUTES } from 'lib/page-routes';
import { useApp } from 'lib/contexts/save-in-app-context';
import { getCurrency, durationType } from 'utils/format';
import styles from './MessageModal.module.scss';

export interface MessageModalProps {
  isOpen: boolean;
  onClose: () => void;
  student_society_id: string;
  societyName: string;
  sponsorshipPlanSelected: SponsorShipPlanType | null;
}

export const MessageModal: React.FC<MessageModalProps> = (
  props: MessageModalProps
) => {
  const router = useRouter();
  const notificationInstance = useNotification();

  const {
    isOpen,
    onClose,
    student_society_id,
    societyName,
    sponsorshipPlanSelected,
  } = props;
  const { isLoggedIn } = useAuth();
  const [message, setMessage] = useState<string>('');
  const { setRecruiterMessage } = useApp();
  const { isLoading, sendSponsorshipOfferMessage } = useChatRoom();

  const onSendMessageClick = async () => {
    if (isLoggedIn) {
      try {
        const response = await sendSponsorshipOfferMessage({
          student_society_id,
          message,
          sponsorship_plan_id: sponsorshipPlanSelected?.id || null,
        });
        if (response) {
          onClose();
        }
      } catch (error) {
        notificationInstance.error(error as Error);
      }
    } else {
      setRecruiterMessage({
        sponsorship_student_society_id: student_society_id,
        sponsorship_message: message,
        sponsorship_plan_id: sponsorshipPlanSelected?.id || null,
      });

      router.push(`${PAGE_ROUTES.RECRUITER_SIGN_UP}`);
    }
  };

  const planDetails = useMemo(() => {
    if (sponsorshipPlanSelected) {
      const {
        title,
        custom_features,
        huzzle_features,
        cost,
        duration_type,
        currency,
      } = sponsorshipPlanSelected;
      const filterCustomFeatures = custom_features.filter(
        (item) => item !== ' '
      );
      const features = [...huzzle_features, ...filterCustomFeatures];
      const description = features?.join(', ');
      return (
        <div className={styles.planDetails}>
          <Row justify="space-between" wrap={false}>
            <p className={styles.planTitle}>{title}</p>
            {cost ? (
              <p className={styles.planCost}>
                {getCurrency(currency)}
                {cost} <span>{durationType(duration_type)}</span>
              </p>
            ) : null}
          </Row>
          <p className={styles.planDesc}>{description}</p>
        </div>
      );
    }
  }, [sponsorshipPlanSelected]);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentClassName={styles.MessageModalContainer}
    >
      <div className={cn(styles.MessageModalContent)}>
        <p className={styles.title}>Message {societyName}</p>
        <IconButton
          size="small"
          iconName="close"
          className={styles.closeIcon}
          onClick={onClose}
        />
        {sponsorshipPlanSelected ? planDetails : null}
        <TextArea
          rows={10}
          textAreaClassName={styles.textEditor}
          onChange={(e) => setMessage(e.target.value)}
        />
      </div>
      <div className={cn(styles.MessageModalSeparator)}></div>
      <div className={cn(styles.MessageModalFooter)}>
        <ButtonV3
          color="primary"
          disabled={!message}
          onClick={onSendMessageClick}
          isLoading={isLoading}
        >
          Send message
        </ButtonV3>
      </div>
    </Modal>
  );
};

MessageModal.displayName = 'MessageModal';
